import type { ReactNode } from "react";
import {
  EmptyMachine,
  type MachineArt,
} from "@volca/ui/components/ui/empty-machine";
import { FloorGrid } from "@volca/ui/components/ui/floor-grid";
import { Stack } from "@volca/ui/components/ui/stack";
import { Heading, Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";

const ENTER =
  "animate-in fade-in slide-in-from-bottom-3 blur-in-xs fill-mode-both duration-300 ease-out motion-reduce:slide-in-from-bottom-0 motion-reduce:blur-in-[0px]";

interface EmptyPageProps {
  art: MachineArt;
  active?: boolean | "starting";
  step?: string;
  title: ReactNode;
  tagline?: ReactNode;
  action?: ReactNode;
  secondaryAction?: ReactNode;
  footnote?: ReactNode;
  bleed?: boolean;
  role?: "alert";
  className?: string;
}

export const EmptyPage = ({
  art,
  active,
  step,
  title,
  tagline,
  action,
  secondaryAction,
  footnote,
  bleed,
  role,
  className,
}: EmptyPageProps) => (
  <div
    data-machine-host
    role={role}
    className={cn(
      "@container/empty-page relative isolate flex min-h-full w-full flex-auto shrink-0 overflow-hidden",
      {
        "-mx-4 -mb-8 w-[calc(100%+2rem)] md:-mx-8 md:w-[calc(100%+4rem)]":
          bleed,
      },
      className,
    )}
  >
    <FloorGrid floor={art.floor} className="-z-10" />
    <Stack
      gap={0}
      justify="center"
      className={cn(
        "w-full flex-auto shrink-0 px-6 py-8 @lg/empty-page:px-10 @4xl/empty-page:grid @4xl/empty-page:grid-cols-12 @4xl/empty-page:grid-rows-1 @4xl/empty-page:gap-x-6 @4xl/empty-page:py-12",
        { "@3xl/empty-page:px-16": bleed },
      )}
    >
      {/* Above the copy on small screens. On large ones it takes the right-hand
        columns, near the top, with room above the flue for its heat shimmer. */}
      <Stack
        className="mb-8 w-full shrink-0 @4xl/empty-page:col-span-5 @4xl/empty-page:col-start-8 @4xl/empty-page:row-start-1 @4xl/empty-page:mt-2 @4xl/empty-page:mb-0 @4xl/empty-page:self-start"
        align="center"
        gap={0}
      >
        <EmptyMachine
          art={art}
          active={active}
          className="h-64 w-auto max-w-full @sm/empty-page:h-72 @xl/empty-page:h-80 @4xl/empty-page:h-auto @4xl/empty-page:w-full @7xl/empty-page:max-w-128"
        />
      </Stack>
      {/* On large screens the copy column fills the height: the headline and
        actions sit a little above centre and the second path lower down,
        spaced 3 : 2 : 1 by the empty rows around them. */}
      <Stack
        gap={0}
        align="start"
        className="max-w-xl @4xl/empty-page:col-span-6 @4xl/empty-page:col-start-1 @4xl/empty-page:row-start-1 @4xl/empty-page:self-stretch @5xl/empty-page:col-start-2 @7xl/empty-page:col-span-5 @7xl/empty-page:col-start-3"
      >
        <Stack
          aria-hidden
          className="hidden @4xl/empty-page:flex @4xl/empty-page:flex-3"
        />
        <div aria-live="polite" className="w-full">
          <Heading
            key={step}
            level={2}
            variant="display"
            className={cn("text-3xl leading-tight text-balance", ENTER)}
          >
            <span className="block">{title}</span>
            {tagline && (
              <span className={cn("block text-subtle", ENTER, "delay-100")}>
                {tagline}
              </span>
            )}
          </Heading>
        </div>
        {(action || secondaryAction) && (
          <Stack
            direction="row"
            align="center"
            gap={2}
            className={cn("mt-6 flex-wrap", ENTER, "delay-200")}
          >
            {action && (
              <span data-machine-trigger className="contents">
                {action}
              </span>
            )}
            {secondaryAction}
          </Stack>
        )}
        <Stack
          aria-hidden
          className="hidden @4xl/empty-page:flex @4xl/empty-page:flex-2"
        />
        {footnote && (
          <Text
            key={step}
            tone="muted"
            className={cn(
              "mt-12 max-w-md @4xl/empty-page:mt-10",
              ENTER,
              "delay-300",
            )}
          >
            {footnote}
          </Text>
        )}
        <Stack
          aria-hidden
          className="hidden @4xl/empty-page:flex @4xl/empty-page:flex-1"
        />
      </Stack>
    </Stack>
  </div>
);
